/**
 * Simple markdown formatter for coach notes and summaries
 * Handles basic markdown syntax without external dependencies
 */

/**
 * Format markdown text to HTML
 * @param {string} text - Raw markdown text
 * @returns {string} - HTML string
 */
export function formatMarkdown(text) {
  if (!text) return '';

  let html = text;

  // Convert headers (## Header -> <h2>Header</h2>)
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Convert bold (**text** or __text__ -> <strong>text</strong>)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Convert italic (*text* or _text_ -> <em>text</em>)
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Convert links ([text](url) -> <a href="url">text</a>)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Convert unordered lists (- item or * item -> <ul><li>item</li></ul>)
  html = html.replace(/^[*-] (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

  // Convert ordered lists (1. item -> <ol><li>item</li></ol>)
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  // Wrap consecutive <li> tags that aren't already wrapped
  html = html.replace(/(?<!<ul>)(<li>(?:(?!<ul>|<ol>).)*<\/li>\n?)+(?!<\/ul>)/g, function(match) {
    // Check if this is part of an unordered list
    if (/<ul>/.test(html.substring(0, html.indexOf(match)))) {
      return match;
    }
    return '<ol>' + match + '</ol>';
  });

  // Convert line breaks (preserve newlines)
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');

  // Wrap in paragraph tags if not already wrapped
  if (!html.startsWith('<h') && !html.startsWith('<ul') && !html.startsWith('<ol') && !html.startsWith('<p>')) {
    html = '<p>' + html + '</p>';
  }

  return html;
}

/**
 * React component wrapper for rendering markdown
 * @param {Object} props
 * @param {string} props.content - Markdown content
 * @param {string} props.className - Optional CSS class
 */
export function MarkdownContent({ content, className = '' }) {
  const html = formatMarkdown(content);

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
